import { useMemo } from 'react';

import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { useCoverageRules } from '@/shared/hooks';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import { useDebounce } from '@/utils/useDebounce';
import { useTrackScreeningLabelsLoaded } from './useTrackScreeningLabelsLoaded';
import {
  buildDynamicSmbCoverageOptions,
  buildStaticSmbCoverageOptions,
  findSmbCoverageOption,
} from '../utils/coverage.util';

const formatDateForApi = (date?: Date) => {
  if (!date) return undefined;

  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

type UseSmbCoverageOptionsProps = {
  propertyId?: string;
  leaseStart?: Date;
  leaseEnd?: Date;
  grossMonthlyRent?: number;
  prepaidRent?: number;
  freeRent?: number;
  leaseDurationMonths: number;
  selectedValue?: number | string;
  enabled?: boolean;
};

export const useSmbCoverageOptions = ({
  propertyId,
  leaseStart,
  leaseEnd,
  grossMonthlyRent,
  prepaidRent,
  freeRent,
  leaseDurationMonths,
  selectedValue,
  enabled = true,
}: UseSmbCoverageOptionsProps) => {
  const { isFeatureFlagEnabled: isDynamicCoveragesFeatureFlagEnabled } = useSplitFeatureFlag(
    SplitFeatureFlag.OTTERS_4472_DYNAMIC_COVERAGES_INVITE,
  );

  const debouncedLeaseStartDate = useDebounce(formatDateForApi(leaseStart), 100);
  const debouncedLeaseEndDate = useDebounce(formatDateForApi(leaseEnd), 100);
  const debouncedGrossMonthlyRent = useDebounce(grossMonthlyRent, 100);
  const debouncedPrepaidRent = useDebounce(prepaidRent, 100);
  const debouncedFreeRent = useDebounce(freeRent, 100);

  const pricingParams = useMemo(
    () =>
      debouncedLeaseStartDate && debouncedLeaseEndDate && debouncedGrossMonthlyRent
        ? {
            lease_start_date: debouncedLeaseStartDate,
            lease_end_date: debouncedLeaseEndDate,
            gross_monthly_rent: debouncedGrossMonthlyRent,
            prepaid_rent: debouncedPrepaidRent,
            free_rent: debouncedFreeRent,
          }
        : undefined,
    [
      debouncedFreeRent,
      debouncedGrossMonthlyRent,
      debouncedLeaseEndDate,
      debouncedLeaseStartDate,
      debouncedPrepaidRent,
    ],
  );

  const {
    data: coverageRulesFromApi,
    isLoading,
    isError,
    error,
  } = useCoverageRules(propertyId ?? '', Boolean(enabled && propertyId), {
    params: isDynamicCoveragesFeatureFlagEnabled ? pricingParams : undefined,
    waitForRequiredParams: isDynamicCoveragesFeatureFlagEnabled,
  });

  const dynamicCoverageOptions = useMemo(
    () => buildDynamicSmbCoverageOptions(coverageRulesFromApi),
    [coverageRulesFromApi],
  );

  const coverageOptions = useMemo(
    /**
     * When DDS omits pricing we intentionally preserve the legacy SMB radios.
     * That keeps the form usable in environments/properties where pricing is
     * unavailable. The FE calculations are not great, this should not happen ever.
     */
    () =>
      dynamicCoverageOptions.length > 0
        ? dynamicCoverageOptions
        : buildStaticSmbCoverageOptions({
            grossMonthlyRent,
            freeRent,
            prepaidRent,
            leaseDurationMonths,
          }),
    [dynamicCoverageOptions, freeRent, grossMonthlyRent, leaseDurationMonths, prepaidRent],
  );

  const selectedCoverageOption = useMemo(
    () => findSmbCoverageOption(coverageOptions, selectedValue),
    [coverageOptions, selectedValue],
  );

  useTrackScreeningLabelsLoaded({
    propertyId,
    isLoading,
    isError,
    error,
    hasCustomScreeningStatusLabels: false,
    labelCount: coverageOptions.length,
  });

  return {
    coverageOptions,
    selectedCoverageOption,
    isUsingDynamicPricing: dynamicCoverageOptions.length > 0,
    isLoading,
  };
};
